import type { ApiClient } from '@carbonwebmail/api-client'
import type { ContactFieldDto } from '@carbonwebmail/contracts'
import { Dialog } from '../../ui/Dialog.js'
import { Field } from '../../ui/Field.js'
import { Problems } from '../../ui/Problems.js'
import { Declared } from '../contacts/ContactFields.js'
import { reachLabel } from './describeSegment.js'
import { RuleRow } from './RuleRow.js'
import { isKeyedGroup, useSegmentBuilder } from './useSegmentBuilder.js'

/**
 * Making a segment, over the board rather than instead of it.
 *
 * It was a screen at `/segments/new`, which meant leaving the list to add to it
 * and coming back to find it unchanged — the same reason New contact stopped
 * being `/contacts/new`. The board reloads when a segment is really created,
 * because the thing somebody came here to see is the new row in the table.
 *
 * **Every field on one form, one per row**: the name, every column this
 * organisation declared on its segments, and the rules. The declared ones go
 * through the same `Declared` control the contact form and the edit dialog use,
 * so a column cannot be asked for one way here and another way there.
 *
 * **The rules are on the form, and that is the point.** A segment saved with no
 * rules reaches *everyone* — `segmentToSql` answers an empty rule list with
 * EVERYONE — so a dialog that collected a name and left the audience for later
 * would quietly create something that mails the whole list. It opens on one
 * rule rather than none for the same reason the builder does: an empty form
 * asks "what now", one rule shows the shape of the answer.
 *
 * **No groups here.** The builder has "any of" brackets; this is a flat list,
 * all of which must hold. Somebody who needs a bracket is building something
 * worth opening the builder for, and a dialog that grew a second nested rule
 * editor would be the second copy this repository exists to avoid — the row
 * itself is `RuleRow`, shared with the builder.
 *
 * The reach is shown for the same reason the builder shows it: a rule list is
 * abstract until a number moves beside it, and after the send is the expensive
 * time to learn it reached twelve thousand people.
 */
export function NewSegmentDialog({
  client,
  fields,
  onCreated,
  onClose,
}: {
  readonly client: ApiClient
  /** The columns declared on segments, so the form asks for every one of them. */
  readonly fields: readonly ContactFieldDto[]
  readonly onCreated: () => void
  readonly onClose: () => void
}) {
  const builder = useSegmentBuilder({
    client,
    initial: BLANK,
    segmentId: undefined,
    onSaved: () => {
      onCreated()
      onClose()
    },
  })

  const { state } = builder
  const busy = state.saving

  return (
    <Dialog
      footer={
        <>
          <button
            className="rounded-pill bg-brand px-5 py-2.5 font-bold text-[13px] text-brand-contrast disabled:cursor-not-allowed disabled:opacity-60"
            disabled={busy || state.name.trim() === ''}
            form="new-segment"
            type="submit"
          >
            {busy ? 'Adding…' : 'Add segment'}
          </button>
          <button
            className="font-medium text-[13px] text-t3 hover:text-t1"
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
        </>
      }
      heading="New segment"
      onClose={onClose}
    >
      <Problems problems={state.problems} />

      <form
        id="new-segment"
        onSubmit={(event) => {
          event.preventDefault()
          void builder.save()
        }}
      >
        <Field
          disabled={busy}
          hint="What this audience is called. It never changes who it reaches."
          id="new-segment-name"
          label="Name"
          onChange={builder.setName}
          type="text"
          value={state.name}
        />

        {fields.map((field) => (
          <Declared
            busy={busy}
            field={field}
            id={`new-segment-${field.key}`}
            key={field.key}
            onChange={(value) => builder.setCustom(field.key, value)}
            value={state.custom?.[field.key] ?? ''}
          />
        ))}

        <fieldset className="mt-5 border-line border-t pt-4">
          <legend className="mb-2 font-semibold text-[12px] text-t2">Who it reaches</legend>

          <div className="space-y-2">
            {state.children.map((keyed, index) =>
              /*
                A group can only get here if one was already on the segment,
                which a new one cannot have — but the state allows it, so it is
                skipped rather than rendered half-drawn. The builder is where a
                bracket is edited.
              */
              isKeyedGroup(keyed) ? null : (
                <RuleRow
                  declared={builder.declared}
                  joiner={index === 0 ? 'Where' : 'and'}
                  key={keyed.key}
                  onChange={(patch) => builder.setRule(keyed.key, patch)}
                  onRemove={() => builder.removeRule(keyed.key)}
                  rule={keyed.rule}
                />
              ),
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <button
              className="rounded-field border border-line bg-surface px-2.5 py-1.5 font-medium text-[12px] text-t2 hover:text-t1"
              onClick={builder.addRule}
              type="button"
            >
              Add rule
            </button>
            {/*
              A dash while unknown, never a zero: zero is an answer — "this
              reaches nobody" — and showing it before the count arrives would
              talk somebody out of a segment that is fine.
            */}
            <p className="text-[12px] text-t3">
              Reaches{' '}
              <span className="font-num font-semibold text-t1 tabular-nums">
                {state.reach === undefined ? '—' : reachLabel(state.reach)}
              </span>{' '}
              right now
            </p>
          </div>

          {state.children.length === 0 ? (
            <p className="mt-2 rounded-field border border-danger bg-danger-soft px-2 py-1.5 text-[12px] text-danger">
              With no rules this reaches everyone on your list who is still subscribed.
            </p>
          ) : null}
        </fieldset>
      </form>
    </Dialog>
  )
}

/**
 * One rule rather than none, and `custom` present rather than absent.
 *
 * Present means the save carries the declared columns, which is what makes the
 * segment and its column values one write instead of two that can half-happen.
 * The builder page passes no `custom` at all, and `useSegmentBuilder` says why.
 */
const BLANK = {
  name: '',
  definition: {
    match: 'all' as const,
    children: [{ field: 'status' as const, operator: 'is' as const, value: 'subscribed' }],
  },
  custom: {},
}
